﻿import {computed, type ComputedRef, defineComponent, ref, watch} from 'vue';
import { debounce } from "lodash-es";
import type {CodeListValueOrder} from "@/app/shared/helpers/code-list-value-order.ts";
import {SearchOrganisationFilters, type ISearchOrganisationQuery} from "@/app/stores/organisation-store.ts";
import {SearchSessionsQuery} from "@/api/enterprise-gateway-api.proxy.ts";

export default defineComponent({
    name: 'AegSearchOrganisation',
    emits: {
        search: (query: ISearchOrganisationQuery) => true
    },
    props: {
        defaultQuery: {
            type: Object as () => ISearchOrganisationQuery,
            required: false
        },
    },
    setup: function (props, {emit}) {
        const searchFields: CodeListValueOrder[] = [
            <CodeListValueOrder>{
                isDefaultValue: true,
                key: SearchOrganisationFilters.Kbo,
                label: 'KBO'
            },
            <CodeListValueOrder>{
                key:  SearchOrganisationFilters.Ovo,
                label: 'OVO-code'
            },
            <CodeListValueOrder>{
                key: SearchOrganisationFilters.OrgCode,
                label: 'ORG-code (buitenlandse org)'
            },
            <CodeListValueOrder>{
                key: SearchOrganisationFilters.SubjectId,
                label: 'SubjectID (Particulier)'
            }
        ];

        const keyword = ref<string>('');
        const searchField = ref<string>(SearchOrganisationFilters.Kbo);

        const uniqueFieldId = (id: string) => {
            return `${id}-${crypto.randomUUID()}`;
        }

        const keywordPlaceHolder = computed(() => {
           if(searchField.value === SearchOrganisationFilters.Kbo) {
               return 'Typ een KBO-nummer...';
           } else if(searchField.value === SearchOrganisationFilters.Ovo) {
               return 'Typ een OVO-code...';
           } else if(searchField.value === SearchOrganisationFilters.OrgCode) {
               return 'Typ een ORG-code...';
           } else if(searchField.value === SearchOrganisationFilters.SubjectId) {
               return 'Typ een SubjectID...';
           }
        });

        const watchFields: ComputedRef<ISearchOrganisationQuery> = computed(() => {
            return {
                type: searchField.value as SearchOrganisationFilters,
                keyword: keyword.value
            };
        });

        const debouncedSearch = debounce(async () => {
            emit('search', watchFields.value);
        }, 100);

        const searchFieldChanged = () => {
            if(keyword.value.length === 0) {
                return;
            }
            debouncedSearch();
        }

        const search = () => {
            emit('search', watchFields.value);
        }

        watch(watchFields, searchFieldChanged, {immediate: false, deep: true});
        watch(() => props.defaultQuery, (newDefaultQuery) => {
            if (newDefaultQuery) {
                keyword.value = newDefaultQuery.keyword;
                searchField.value = newDefaultQuery.type?.toString() ?? SearchOrganisationFilters.Kbo;
            }
        }, {immediate: true, deep: true});

        return {
            searchFields,
            searchField,
            keyword,
            keywordPlaceHolder,
            uniqueFieldId,
            search,
        }
    }
})
